import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const ClientFormModal = ({ isOpen, onClose, client, language, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    status: 'active',
    practiceArea: 'civil',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (client) {
      setFormData({
        name: client?.name || '',
        email: client?.email || '',
        phone: client?.phone || '',
        address: client?.address || '',
        city: client?.city || '',
        postalCode: client?.postalCode || '',
        status: client?.status || 'active',
        practiceArea: client?.practiceAreaValue || 'civil',
        notes: client?.notes || ''
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        postalCode: '',
        status: 'active',
        practiceArea: 'civil',
        notes: ''
      });
    }
    setErrors({});
  }, [client, isOpen]);

  const statusOptions = [
    { value: 'active', label: language === 'ar' ? 'نشط' : language === 'fr' ? 'Actif' : 'Active' },
    { value: 'inactive', label: language === 'ar' ? 'غير نشط' : language === 'fr' ? 'Inactif' : 'Inactive' },
    { value: 'pending', label: language === 'ar' ? 'قيد الانتظار' : language === 'fr' ? 'En attente' : 'Pending' }
  ];

  const practiceAreaOptions = [
    { value: 'civil', label: language === 'ar' ? 'القانون المدني' : language === 'fr' ? 'Droit civil' : 'Civil Law' },
    { value: 'criminal', label: language === 'ar' ? 'القانون الجنائي' : language === 'fr' ? 'Droit pénal' : 'Criminal Law' },
    { value: 'commercial', label: language === 'ar' ? 'القانون التجاري' : language === 'fr' ? 'Droit commercial' : 'Commercial Law' },
    { value: 'family', label: language === 'ar' ? 'قانون الأسرة' : language === 'fr' ? 'Droit de la famille' : 'Family Law' },
    { value: 'labor', label: language === 'ar' ? 'قانون العمل' : language === 'fr' ? 'Droit du travail' : 'Labor Law' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = language === 'ar' ? 'الاسم مطلوب' : language === 'fr' ? 'Le nom est requis' : 'Name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = language === 'ar' ? 'البريد الإلكتروني مطلوب' : language === 'fr' ? "L'email est requis" : 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = language === 'ar' ? 'البريد الإلكتروني غير صالح' : language === 'fr' ? 'Email invalide' : 'Invalid email';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = language === 'ar' ? 'رقم الهاتف مطلوب' : language === 'fr' ? 'Le téléphone est requis' : 'Phone is required';
    } else if (!/^(\+212|0)[5-7]\d{8}$/?.test(formData?.phone?.replace(/\s/g, ''))) {
      newErrors.phone = language === 'ar' ? 'رقم هاتف مغربي غير صالح' : language === 'fr' ? 'Numéro marocain invalide' : 'Invalid Moroccan phone';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave(formData);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">
            {client
              ? language === 'ar' ? 'تعديل العميل' : language === 'fr' ? 'Modifier le client' : 'Edit Client'
              : language === 'ar' ? 'إضافة عميل جديد' : language === 'fr' ? 'Ajouter un client' : 'Add New Client'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="px-6 py-4 space-y-4">
            <Input
              label={language === 'ar' ? 'الاسم الكامل' : language === 'fr' ? 'Nom complet' : 'Full Name'}
              type="text"
              required
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
              error={errors?.name}
              placeholder={language === 'ar' ? 'أدخل الاسم الكامل' : language === 'fr' ? 'Entrez le nom complet' : 'Enter full name'}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={language === 'ar' ? 'البريد الإلكتروني' : language === 'fr' ? 'Email' : 'Email'}
                type="email"
                required
                value={formData?.email}
                onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                error={errors?.email}
                placeholder="client@example.com"
              />

              <Input
                label={language === 'ar' ? 'رقم الهاتف' : language === 'fr' ? 'Téléphone' : 'Phone'}
                type="tel"
                required
                value={formData?.phone}
                onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
                error={errors?.phone}
                placeholder="+212 6XX XXX XXX"
              />
            </div>

            <Input
              label={language === 'ar' ? 'العنوان' : language === 'fr' ? 'Adresse' : 'Address'}
              type="text"
              value={formData?.address}
              onChange={(e) => setFormData({ ...formData, address: e?.target?.value })}
              placeholder={language === 'ar' ? 'أدخل العنوان' : language === 'fr' ? "Entrez l'adresse" : 'Enter address'}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={language === 'ar' ? 'المدينة' : language === 'fr' ? 'Ville' : 'City'}
                type="text"
                value={formData?.city}
                onChange={(e) => setFormData({ ...formData, city: e?.target?.value })}
                placeholder={language === 'ar' ? 'الدار البيضاء' : language === 'fr' ? 'Casablanca' : 'Casablanca'}
              />

              <Input
                label={language === 'ar' ? 'الرمز البريدي' : language === 'fr' ? 'Code postal' : 'Postal Code'}
                type="text"
                value={formData?.postalCode}
                onChange={(e) => setFormData({ ...formData, postalCode: e?.target?.value })}
                placeholder="20000"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label={language === 'ar' ? 'الحالة' : language === 'fr' ? 'Statut' : 'Status'}
                options={statusOptions}
                value={formData?.status}
                onChange={(value) => setFormData({ ...formData, status: value })}
              />

              <Select
                label={language === 'ar' ? 'مجال الممارسة' : language === 'fr' ? 'Domaine de pratique' : 'Practice Area'}
                options={practiceAreaOptions}
                value={formData?.practiceArea}
                onChange={(value) => setFormData({ ...formData, practiceArea: value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {language === 'ar' ? 'ملاحظات' : language === 'fr' ? 'Notes' : 'Notes'}
              </label>
              <textarea
                className="w-full px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background transition-all resize-none"
                rows="4"
                value={formData?.notes}
                onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                placeholder={language === 'ar' ? 'أدخل أي ملاحظات إضافية' : language === 'fr' ? 'Entrez des notes supplémentaires' : 'Enter additional notes'}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted/30">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              {language === 'ar' ? 'إلغاء' : language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Save"
              iconPosition="left"
              iconSize={16}
            >
              {language === 'ar' ? 'حفظ' : language === 'fr' ? 'Enregistrer' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientFormModal;